import { Button, Checkbox } from '@atgfr8/atg-ui-tailwind';
import { closeModal } from '@store/modals/store';
import { For, JSXElement } from 'solid-js';

import {
  insuranceProduct,
  loadSureAcknowledgmentModal,
} from './LoadSureHelpers';
import {
  LoadSureExclusionsLink,
  LoadSureTermsLink,
} from './LoadSureLegalLinks';

type LoadSureAcknowledgmentModalProps = {
  acknowledgments: boolean[];
  selectedQuote: insuranceProduct | null;
  onAcknowledgmentsChange: (next: boolean[]) => void;
  onContinue: () => void;
};

const SECURITY_INSTRUCTIONS = [
  'Use restroom facilities or obtain a quick meal.',
  'Request repair or towing assistance following a breakdown.',
  'Contact dispatch or seek support in the event of sudden illness.',
];

const ACKNOWLEDGMENT_COUNT = 3;

export const emptyAcknowledgments = () =>
  new Array<boolean>(ACKNOWLEDGMENT_COUNT).fill(false);

export const LoadSureAcknowledgmentModal = (
  props: Readonly<LoadSureAcknowledgmentModalProps>,
) => {
  const allAcknowledged = () =>
    props.acknowledgments.every((acknowledged) => acknowledged);

  const toggle = (index: number, checked: boolean) => {
    props.onAcknowledgmentsChange(
      props.acknowledgments.map((value, i) => (i === index ? checked : value)),
    );
  };

  const handleCancel = () => {
    closeModal(loadSureAcknowledgmentModal);
  };

  const acknowledgmentRow = (
    index: number,
    dataTestId: string,
    children: JSXElement,
  ) => (
    <div class="flex items-start gap-1 my-3">
      <Checkbox
        checked={props.acknowledgments[index]}
        color="info"
        class="ml-[-9px]"
        onChange={(checked) => toggle(index, checked)}
        dataTestId={dataTestId}
      />
      <div class="text-sm text-[#2B2B2B]">{children}</div>
    </div>
  );

  return (
    <div
      class="flex flex-col overflow-hidden rounded-[5px]"
      data-testid="loadsure-acknowledgment-modal"
    >
      <div class="bg-[#F9FAFC] border-b border-[#ECECEE] px-6 py-3">
        <div class="text-lg font-semibold text-[#2B2B2B]">
          Loadsure Coverage Acknowledgment Required
        </div>
      </div>

      <div class="px-6 py-5">
        <div class="text-sm text-[#2B2B2B]">
          Before proceeding with the Loadsure purchase, please review and
          acknowledge the following requirements.
        </div>

        {acknowledgmentRow(
          0,
          'loadsure-acknowledge-terms-checkbox',
          <>
            I confirm that I have read and understand the Loadsure{' '}
            <LoadSureExclusionsLink quote={props.selectedQuote} /> and{' '}
            <LoadSureTermsLink
              quote={props.selectedQuote}
              label="Terms & Conditions"
            />
            .
          </>,
        )}

        {acknowledgmentRow(
          1,
          'loadsure-acknowledge-tracking-checkbox',
          <>
            I confirm that this load will be tracked using MacroPoint, Trucker
            Tools, or an equivalent tracking solution.
          </>,
        )}

        {acknowledgmentRow(
          2,
          'loadsure-acknowledge-security-checkbox',
          <>
            I confirm that written security instructions will be provided to the
            carrier, including:
          </>,
        )}

        <div class="mt-2 ml-7 bg-[#EAF3FA] border border-[#D5DDE7] rounded px-5 py-4 text-sm text-[#2B2B2B]">
          <ul class="list-disc pl-5 space-y-2">
            <li>
              The vehicle must not be left unattended except for brief periods
              to:
              <ul class="[list-style-type:circle] pl-5 mt-2 space-y-2">
                <For each={SECURITY_INSTRUCTIONS}>
                  {(instruction) => <li>{instruction}</li>}
                </For>
              </ul>
            </li>
            <li>
              During any permitted stop, vehicle doors must remain locked,
              windows fully closed, and keys must not be left in the vehicle.
            </li>
          </ul>
        </div>

        <div class="mt-5 text-sm italic text-[#707070]">
          All acknowledgments are required before proceeding.
        </div>
      </div>

      <div class="bg-[#F9FAFC] border-t border-[#ECECEE] px-6 py-3 flex justify-end gap-2">
        <Button
          type="button"
          onClick={handleCancel}
          dataTestId="loadsure-acknowledge-cancel-button"
        >
          CANCEL
        </Button>
        <Button
          type="button"
          variant="contained"
          color="info"
          disabled={!allAcknowledged()}
          onClick={() => props.onContinue()}
          dataTestId="loadsure-acknowledge-continue-button"
        >
          CONTINUE
        </Button>
      </div>
    </div>
  );
};
